/* eslint-disable react/jsx-curly-brace-presence */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore TODO: add filesize declaration to @types
import filesize from 'filesize';
import SectionMessage from '@zola/zola-ui/src/components/SectionMessage';
import { toastsActions } from '@zola-helpers/client';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { Link } from '@zola/zola-ui/src/components/Link';
import type { WEventView } from '@zola/svc-web-api-ts-client';
import { DropdownV2 } from '@zola/zola-ui/src/components/Form/Dropdown';
// @ts-ignore TODO: add zola-ui RadioField to @types
import { RadioField } from '@zola/zola-ui/src/components/Form/RadioField';
import Loader from 'components/common/ui/Loader';
import { getUserIsGuest } from 'selectors/user/userSelectors';
import { openModal, hideModal } from 'cards/actions/modalActions';
import {
  uploadGuestListCSV,
  importGuestListCSV,
  exportGuestListCSV,
  bulkDeleteGuestList,
  getWeddingEvents,
  getPrefilledCSVTemplate,
  importGuestList,
} from 'cards/util/placecardGuestList';
import { fetchProject } from 'cards/actions/projectActions';
import { receivePlacecardGuestList } from 'cards/actions/placecardGuestList';
import { getProjectUUID } from 'cards/selectors/cardProjectSelector';
import { getPlacecardGuestList } from 'cards/selectors/placecardGuestList';
import needHelpIcon from 'assets/images/invitations/need-help-icon.svg';
import downloadTemplateIcon from 'assets/images/invitations/download-template-icon.svg';
import greenCheckIcon from 'assets/images/invitations/green-check-success-icon.svg';
import fileDropzoneIcon from 'assets/images/invitations/file-dropzone-icon.svg';
import styles from './styles.module.less';

export const IMPORT_MSG_POSITIVE = '💥 Bam! We’ve imported all your guest details.';

export const IMPORT_MSG_NEGATIVE = 'There was a problem importing your spreadsheet.';

export const LOCALSTORAGE_KEY = 'ZOLA_INVITES_PLACECARD_GUEST_LIST_TEMPLATE_EXPORT_FTE';

export interface Step {
  title: string;
  component?: (props: StepProps) => JSX.Element | null;
  order: number;
}

export interface StepProps {
  currentStep: Step;
  nextEnabled: boolean;
  setNextEnabled: (enabled: boolean) => void;
  setCurrentStepKey: (key: string) => void;
}

export interface TemplateForm {
  isPrefilled: boolean;
  eventUuid: string | null;
  grouping: string | null;
  enforceRSVP: boolean;
}

export interface TemplateEventData {
  initialized: boolean;
  events: WEventView[] | null;
}

export interface TemplateRadioProps {
  form: TemplateForm;
  setForm(form: TemplateForm): void;
}

const TemplateRadio = ({ form, setForm }: TemplateRadioProps): JSX.Element | null => {
  const [eventData, setEventData] = useState<TemplateEventData>({
    initialized: false,
    events: null,
  });

  const hasRSVP = useMemo(
    () =>
      form.eventUuid &&
      !!eventData?.events?.find(event => event.uuid === form.eventUuid)?.collect_rsvps,
    [form, eventData]
  );

  const eventOptions = useMemo(
    () => eventData?.events?.map(eventView => ({ label: eventView.name, value: eventView.uuid })),
    [eventData.events]
  );

  useEffect(() => {
    if (eventData.initialized) {
      return;
    }

    getWeddingEvents()
      .then((events?: WEventView[]) => {
        if (!events || events.length === 0) {
          setEventData({
            initialized: true,
            events: null,
          });

          return;
        }

        setEventData({
          initialized: true,
          events,
        });

        setForm({
          ...form,
          eventUuid: events?.[0]?.uuid as string | null,
          enforceRSVP: !!events?.[0]?.collect_rsvps,
        });
      })
      .catch(e => {
        setEventData({
          initialized: true,
          events: null,
        });

        throw e;
      });
  }, [form, setForm, eventData]);

  if (!eventData.initialized) {
    return (
      <div className={styles.loaderBox}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.templateRadioBox}>
      <RadioField
        id="template-blank"
        label="Download a blank Template"
        checked={!form.isPrefilled}
        onChange={() => setForm({ ...form, isPrefilled: false })}
      />
      <RadioField
        id="template-gl-prefill"
        label="Populate your Guest List table with your Zola Guest List"
        checked={form.isPrefilled}
        onChange={() => setForm({ ...form, isPrefilled: true })}
      />
      {form.isPrefilled && (
        <div className={styles.prefilledOptionsBox}>
          {eventData.events && (
            <div className={styles.dropdownBox}>
              <DropdownV2
                id="template-event"
                label="Event"
                options={eventOptions}
                placeholder="Event"
                value={form.eventUuid}
                onSelect={(eventUuid: string) => setForm({ ...form, eventUuid })}
              />
            </div>
          )}
          <div className={styles.templateGroupingBox}>
            <div className={styles.label}>Names on place cards</div>
            <RadioField
              id="template-grouping-individual"
              labelElem={
                <span className={styles.radioLabel}>
                  I want everyone to have their{' '}
                  <span className={styles.radioBold}>own place card</span>
                </span>
              }
              checked={form.grouping === 'EVERYONE_SEPARATE'}
              onChange={() => setForm({ ...form, grouping: 'EVERYONE_SEPARATE' })}
            />
            <RadioField
              id="template-grouping-couples"
              labelElem={
                <span className={styles.radioLabel}>
                  I want my couples to be on the <span className={styles.radioBold}>same card</span>
                </span>
              }
              checked={form.grouping === 'COUPLES_AND_CHILDREN_TOGETHER'}
              onChange={() => setForm({ ...form, grouping: 'COUPLES_AND_CHILDREN_TOGETHER' })}
            />
            {hasRSVP && (
              <div className={styles.rsvpBox}>
                <div className={styles.label}>RSVP Status</div>
                <RadioField
                  id="template-rsvp-enforced"
                  labelElem={
                    <span className={styles.radioLabel}>
                      I only want to include guests who{' '}
                      <span className={styles.radioBold}>RSVP{"'"}d Yes</span>
                    </span>
                  }
                  checked={form.enforceRSVP}
                  onChange={() => setForm({ ...form, enforceRSVP: true })}
                />
                <RadioField
                  id="template-all-guests"
                  labelElem={
                    <span className={styles.radioLabel}>
                      I want <span className={styles.radioBold}>all guests</span>
                    </span>
                  }
                  checked={!form.enforceRSVP}
                  onChange={() => setForm({ ...form, enforceRSVP: false })}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const CSV_TEMPLATE = `GUEST NAME,TABLE
Emily Guest,Table 12
`;

const DownloadStep = ({
  currentStep,
  nextEnabled,
  setNextEnabled,
  setCurrentStepKey,
}: StepProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const projectUuid = useSelector(getProjectUUID);
  const isGuest = useSelector(getUserIsGuest);

  const [prefillForm, setPrefillForm] = useState<TemplateForm>({
    isPrefilled: false,
    eventUuid: null,
    grouping: 'EVERYONE_SEPARATE',
    enforceRSVP: false,
  });

  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const onDownloadTemplate = useCallback(() => {
    setNextEnabled(true);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(true));

    if (!linkRef.current) {
      return;
    }

    if (prefillForm.isPrefilled) {
      getPrefilledCSVTemplate({
        projectUuid,
        eventUuid: prefillForm.eventUuid || undefined,
        grouping: prefillForm.grouping || undefined,
        enforceRSVP: prefillForm.enforceRSVP || undefined,
      })
        .then(prefillTemplate => {
          if (!linkRef.current) {
            return;
          }

          const text =
            prefillTemplate?.text && prefillTemplate.text.length > 0
              ? prefillTemplate.text
              : CSV_TEMPLATE;
          const blob = new Blob([text], {
            type: 'text/plain',
          });
          const objectURL = URL.createObjectURL(blob);
          linkRef.current.href = objectURL;
          linkRef.current.click();
        })
        .catch(e => {
          throw e;
        });

      return;
    }

    const blob = new Blob([CSV_TEMPLATE], {
      type: 'text/plain',
    });
    const objectURL = URL.createObjectURL(blob);
    linkRef.current.href = objectURL;
    linkRef.current.click();
  }, [projectUuid, prefillForm, setNextEnabled]);

  const onNext = useCallback(() => {
    if (prefillForm.isPrefilled) {
      setNextEnabled(false);
      importGuestList({
        projectUuid,
        eventUuid: prefillForm.eventUuid || undefined,
        grouping: prefillForm.grouping || undefined,
        enforceRSVP: prefillForm.enforceRSVP,
      })
        .then(placecardDetail => {
          dispatch(toastsActions.positive({ headline: IMPORT_MSG_POSITIVE }));
          dispatch(receivePlacecardGuestList(placecardDetail));
          dispatch(hideModal());
        })
        .catch(e => {
          dispatch(toastsActions.negative({ headline: IMPORT_MSG_NEGATIVE }));
          dispatch(hideModal());
          throw e;
        });

      return;
    }

    setCurrentStepKey('upload');
    setNextEnabled(false);
  }, [dispatch, projectUuid, prefillForm, setCurrentStepKey, setNextEnabled]);

  useEffect(() => {
    const seen = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) || 'false');

    if (seen) {
      setNextEnabled(true);
    }
  }, [setNextEnabled]);

  return (
    <div className={styles.stepBox}>
      <div className={styles.stepTitle}>{currentStep.title}</div>
      <div className={styles.stepBody}>
        The template helps you format your guest names and table numbers for printing. You can also
        choose to import your existing Zola Guest List instead.
      </div>
      {!isGuest && <TemplateRadio form={prefillForm} setForm={setPrefillForm} />}
      {!prefillForm.isPrefilled && (
        <div className={styles.templateLinkBox}>
          <Link
            className={styles.templateLink}
            role="button"
            target="_blank"
            onClick={onDownloadTemplate}
          >
            <div className={styles.templateLinkText}>
              {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
              <a
                ref={linkRef}
                className={styles.hidden}
                href="#"
                target="_blank"
                download="Zola-Place-Cards-Upload-Template.csv"
                onClick={event => {
                  event?.stopPropagation();
                  event?.nativeEvent?.stopImmediatePropagation();
                }}
              />
              <img src={downloadTemplateIcon} alt="download template" /> Download Guest List
              Template
            </div>
          </Link>
          {nextEnabled && (
            <div className={styles.templateLinkSuccessText}>
              <img src={greenCheckIcon} alt="template download success" /> Great!
            </div>
          )}
        </div>
      )}
      {nextEnabled && !prefillForm.isPrefilled && (
        <div className={styles.templateInstructionsBox}>
          {/* @ts-ignore TODO: fix SectionMessage href type in zola-ui */}
          <SectionMessage
            alt="template instructions"
            arrow={false}
            description={`Open the template, enter your guest and table names and save the file. Do not delete the "Guest Name", "Table"; column headers in the template. it's a CSV file that will open in Microsoft Excel and similar programs. Be sure to follow the template exactly or we won't be able to process your guest and table names!`}
            title="Template Instructions"
            type="Info"
          />
        </div>
      )}
      <div className={styles.stepButtonRow}>
        <div className={styles.needHelp}>
          <img src={needHelpIcon} alt="need help?" />{' '}
          <span className={styles.needHelpText}>Need help?</span>{' '}
          <a className={styles.needHelpLink} href="mailto:invitesfeedback@zola.com">
            Reach out to us.
          </a>
        </div>
        <ButtonV2 disabled={!prefillForm.isPrefilled && !nextEnabled} onClick={onNext}>
          {prefillForm.isPrefilled ? 'Next: Upload Guest List' : 'Next: Upload Template'}
        </ButtonV2>
      </div>
    </div>
  );
};

export interface FileStatus {
  objectUuid: string | null;
  file: File | null;
  uploading: boolean;
}

const UploadStep = ({
  currentStep,
  nextEnabled,
  setNextEnabled,
}: StepProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const projectUuid = useSelector(getProjectUUID);
  const [fileStatus, setFileStatus] = useState<FileStatus>({
    objectUuid: null,
    file: null,
    uploading: false,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const onFileChange = useCallback(
    ({ event, file }: { event?: React.ChangeEvent<HTMLInputElement>; file?: File }) => {
      let uploadFile: File | null = null;
      if (event?.target?.files?.[0]) {
        uploadFile = event?.target?.files?.[0];
      } else if (file) {
        uploadFile = file;
      }

      if (!uploadFile) {
        return;
      }

      setFileStatus({ ...fileStatus, uploading: true });
      uploadGuestListCSV({ file: uploadFile })
        .then(({ objectUuid }) => {
          setFileStatus({ ...fileStatus, file: uploadFile, objectUuid, uploading: false });
          setNextEnabled(true);
        })
        .catch(e => {
          throw e;
        });
    },
    [fileStatus, setNextEnabled]
  );

  const onUpload = useCallback(
    ({
      browseEvent,
      dropEvent,
    }: {
      browseEvent?: React.UIEvent<HTMLAnchorElement>;
      dropEvent?: React.DragEvent<HTMLDivElement>;
    }) => {
      if (fileStatus.uploading) {
        return;
      }

      if (browseEvent && inputRef.current) {
        inputRef.current?.click();
      }

      if (dropEvent) {
        const file = dropEvent.dataTransfer?.files?.[0];

        if (file.name.split('.').pop()?.toLowerCase() !== 'csv') {
          dispatch(toastsActions.negative({ headline: 'Please only upload .csv files.' }));
          return;
        }

        onFileChange({ file });
      }
    },
    [dispatch, fileStatus, onFileChange]
  );

  const onImport = useCallback(() => {
    if (fileStatus.uploading || !fileStatus.objectUuid) {
      return;
    }

    setNextEnabled(false);
    importGuestListCSV({ projectUuid, objectUuid: fileStatus.objectUuid })
      .then(placecardDetail => {
        dispatch(toastsActions.positive({ headline: IMPORT_MSG_POSITIVE }));
        dispatch(receivePlacecardGuestList(placecardDetail));
        return dispatch(fetchProject(projectUuid));
      })
      .then(() => {
        dispatch(hideModal());
      })
      .catch(e => {
        dispatch(toastsActions.negative({ headline: IMPORT_MSG_NEGATIVE }));
        dispatch(hideModal());
        throw e;
      });
  }, [dispatch, projectUuid, fileStatus, setNextEnabled]);

  return (
    <div className={styles.stepBox}>
      <div className={styles.stepTitle}>{currentStep.title}</div>
      <div className={styles.stepBody}>Once {"you've"} filled it out, upload your file below.</div>
      {!fileStatus?.objectUuid && !fileStatus.uploading && (
        <div
          className={styles.fileDropzoneBox}
          onDragOver={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={dropEvent => onUpload({ dropEvent })}
        >
          <img src={fileDropzoneIcon} alt="upload your guest list" />
          <div className={styles.fileDropzoneText}>
            Drag and drop your CSV file here, or{' '}
            <Link
              role="button"
              onClick={(browseEvent: React.UIEvent<HTMLAnchorElement>) => onUpload({ browseEvent })}
            >
              browse
            </Link>
          </div>
        </div>
      )}
      {!fileStatus.file && fileStatus.uploading && (
        <div className={styles.uploadStatusBox}>
          <span role="img" aria-label="uploading">
            &#128150;
          </span>{' '}
          Uploading spreadsheet...
        </div>
      )}
      {fileStatus.file && !fileStatus.uploading && (
        <div className={styles.uploadStatusBox}>
          <img src={greenCheckIcon} alt="uploading" /> {fileStatus.file.name} (
          {filesize(fileStatus.file?.size || 0)})
        </div>
      )}
      <div className={styles.stepButtonRow}>
        <div className={styles.needHelp}>
          <img src={needHelpIcon} alt="need help?" /> Need help?{' '}
          <a className={styles.needHelpLink} href="mailto:invitesfeedback@zola.com">
            Reach out to us.
          </a>
        </div>
        <ButtonV2 disabled={!nextEnabled || !fileStatus.objectUuid} onClick={onImport}>
          Next: Upload Template
        </ButtonV2>
      </div>
      <input
        ref={inputRef}
        className={styles.hidden}
        type="file"
        onChange={event => onFileChange({ event })}
        accept=".csv"
      />
    </div>
  );
};

const STEPS: Record<string, Step> = {
  download: {
    title: '1. Import your Guest List',
    component: DownloadStep,
    order: 0,
  },
  upload: {
    title: '2. Upload template',
    component: UploadStep,
    order: 1,
  },
  import: {
    title: '3. Import',
    order: 2,
  },
};

const Import = (): JSX.Element | null => {
  const [currentStepKey, setCurrentStepKey] = useState<string>('download');
  const [nextEnabled, setNextEnabled] = useState<boolean>(false);

  const currentStep = STEPS[currentStepKey];

  const stepKeys = Object.keys(STEPS).sort((a, b) => STEPS[a].order - STEPS[b].order);

  return (
    <div className={styles.importBox}>
      <div className={styles.importHeader}>
        <div className={styles.importSteps}>
          {stepKeys.map(stepKey => (
            <div key={stepKey} className={styles.stepItem}>
              <div
                className={cx(styles.stepItemName, {
                  [styles.inactive]: currentStep.order < STEPS[stepKey].order,
                })}
              >
                {STEPS[stepKey].title}
              </div>
              <div
                className={cx(styles.stepItemLine, {
                  [styles.inactive]: currentStep.order < STEPS[stepKey].order,
                })}
              />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.importBody}>
        {currentStep.component &&
          React.createElement(currentStep.component, {
            currentStep,
            nextEnabled,
            setNextEnabled,
            setCurrentStepKey,
          })}
      </div>
    </div>
  );
};

const Export = (): JSX.Element | null => {
  const projectUuid = useSelector(getProjectUUID);
  const ref = useRef<HTMLAnchorElement | null>(null);

  const onExport = useCallback(() => {
    if (!ref.current) {
      return;
    }

    exportGuestListCSV({ projectUuid })
      .then(csvString => {
        if (!ref.current) {
          return;
        }

        const blob = new Blob([csvString], {
          type: 'text/plain',
        });
        const objectURL = URL.createObjectURL(blob);
        ref.current.href = objectURL;
        ref.current.click();
      })
      .catch(e => {
        throw e;
      });
  }, [projectUuid]);

  return (
    <div className={styles.exportBox} onClick={onExport}>
      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
      <a
        ref={ref}
        className={styles.hidden}
        href="#"
        target="_blank"
        download="Zola-Place-Cards-Guest-List-Export.csv"
        onClick={event => {
          event?.stopPropagation();
          event?.nativeEvent.stopImmediatePropagation();
        }}
      />
      <div className={styles.exportText}>Export Guest List</div>
    </div>
  );
};

const Clear = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const projectUuid = useSelector(getProjectUUID);
  const guestList = useSelector(getPlacecardGuestList);

  const onClearAll = useCallback(() => {
    if (!guestList?.guest_list) {
      return;
    }
    bulkDeleteGuestList({
      projectUuid,
      guestUuids: guestList?.guest_list
        .map(guest => guest?.uuid)
        .filter(uuid => !!uuid) as string[],
    })
      .then(placecardDetail => {
        dispatch(receivePlacecardGuestList(placecardDetail));
        return dispatch(fetchProject(projectUuid));
      })
      .catch(e => {
        throw e;
      });
  }, [dispatch, projectUuid, guestList]);

  const onClearAllClick = useCallback(() => {
    dispatch(
      openModal(
        'PLACE_CARD_GUEST_LIST_WARNING',
        {
          title: "Heads up! You'll lose your progress.",
          body: 'Are you sure you want to make all your place cards blank?',
          confirmText: 'Yes, Continue',
          cancelText: 'No, Go Back',
          onConfirm: () => {
            onClearAll();
            dispatch(hideModal());
          },
          onCancel: () => {
            dispatch(hideModal());
          },
        },
        { size: 'sm' }
      )
    );
  }, [dispatch, onClearAll]);

  return (
    <div onClick={onClearAllClick} className={styles.clearBox}>
      Clear all
    </div>
  );
};

const Warning = ({
  title,
  body,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmText: string;
  cancelText: string;
  onConfirm(): void;
  onCancel(): void;
}): JSX.Element | null => {
  return (
    <div className={styles.warningBox}>
      <div className={styles.warningTitle}>{title}</div>
      <div className={styles.warningBody}>{body}</div>
      <div className={styles.warningButtonBox}>
        <ButtonV2 className={styles.warningButton} variant="secondary" onClick={onCancel}>
          {cancelText}
        </ButtonV2>
        <ButtonV2 className={styles.warningButton} onClick={onConfirm}>
          {confirmText}
        </ButtonV2>
      </div>
    </div>
  );
};

export default { Import, Export, Clear, Warning, STEPS };
